// components/ModalSwitch.tsx

import React, { useEffect, useState } from 'react';
import './ModalLed.css';
import MQTTService from './MQTTService';
import jwt_decode from 'jwt-decode';

// 변경: slotIndex 등을 함께 저장
type ScheduleItem = {
  text: string;                 // "09:00 ~ 17:00 매일" 같은 표시용 문자열
  slotIndex: number | null;     // 보드에서 내려주는 si / slotIndex (없으면 null)
  startMinutes: number;
  endMinutes: number;
  repeatMode: 'daily' | 'weekly' | string;
  dayOfWeek: number[] | number;
};

interface ModalSwitchProps {
  deviceMac: string;
  onCancel: () => void;
  labelNo: number;
  currentLabel: string;
  mqttMessage: string;
}

const ModalSwitch: React.FC<ModalSwitchProps> = ({
  deviceMac,
  onCancel,
  labelNo,
  currentLabel,
  mqttMessage,
}) => {
  const [email, setEmail] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [repeatMode, setRepeatMode] = useState<'daily' | 'weekly' | string>('daily');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [scheduleLogs, setScheduleLogs] = useState<ScheduleItem[]>([]);
  const pendingDeleteRef = React.useRef<Set<string>>(new Set());

  // slotIndex 포함 키 (기존)
  const makeKey = (it: ScheduleItem) =>
    it.slotIndex != null
      ? `si:${it.slotIndex}`
      : `s:${it.startMinutes}|e:${it.endMinutes}|r:${it.repeatMode}|dow:${JSON.stringify(it.dayOfWeek)}`;

  // slotIndex는 무시하고 "내용"만으로 만드는 키
  const makeContentKey = (it: ScheduleItem) =>
    `s:${it.startMinutes}|e:${it.endMinutes}|r:${it.repeatMode}|dow:${JSON.stringify(it.dayOfWeek)}`;

  // 중복 방지용 ref
  const seenSetRef = React.useRef<Set<string>>(new Set());
  // 첫 번째 list 응답 감지용 ref
  const isFirstListRef = React.useRef(true);

  // MQTT 안전 발행 함수
  const publishSafe = (msg: any) => {
    if (!email) {
      console.warn('email not ready, skip publish:', msg);
      return;
    }
    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, JSON.stringify(msg));
  };

  // MQTT list 응답 처리
  useEffect(() => {
    if (!mqttMessage) return;

    try {
      const msg = JSON.parse(mqttMessage);

      // list 응답 + 내 장치/내 핀만 처리
      const isList = msg.operation === 'list' || msg.o === 'l';
      const mac = msg.mac ?? msg.m;
      const pin = msg.pinIndex ?? msg.i;
      if (!isList || mac !== deviceMac || pin !== labelNo) return;

      // 첫 응답에서만 화면 초기화
      if (isFirstListRef.current) {
        isFirstListRef.current = false;
        setScheduleLogs([]);
        seenSetRef.current.clear();
      }

      // full/축약/배열 응답 모두 수용
      const candidates = Array.isArray(msg.items || msg.list || msg.sl)
        ? (msg.items || msg.list || msg.sl)
        : [msg];

      const dayMap = ['일', '월', '화', '수', '목', '금', '토'];
      const normalizeDow = (v: any) => (Array.isArray(v) ? v : [v]);
      const z = (n: number) => String(n).padStart(2, '0');
      const newItems: ScheduleItem[] = [];

      for (const it of candidates) {
        const sMin = it.startMinutes ?? it.s;
        const eMin = it.endMinutes ?? it.e;
        if (typeof sMin !== 'number' || typeof eMin !== 'number') continue;

        const sH = Math.floor(sMin / 60);
        const sM = sMin % 60;
        const eH = Math.floor(eMin / 60);
        const eM = eMin % 60;

        const startStr = `${z(sH)}:${z(sM)}`;
        const endStr   = `${z(eH)}:${z(eM)}`;

        const repeatModeRaw = it.repeatMode ?? it.r ?? 'daily';
        const repeatModeVal = String(repeatModeRaw).toLowerCase() as 'daily' | 'weekly' | string;
        const dow = it.dayOfWeek ?? it.dow ?? 0;

        const repeatStr =
          repeatModeVal === 'weekly'
            ? normalizeDow(dow).map((d: number) => dayMap[d]).join(', ')
            : '매일';

        const si = it.slotIndex ?? it.si;
        const newItem: ScheduleItem = {
          text: `${startStr} ~ ${endStr} ${repeatStr}`,
          slotIndex: si ?? null,
          startMinutes: sMin,
          endMinutes: eMin,
          repeatMode: repeatModeVal,
          dayOfWeek: dow,
        };

        // ── ① 사용자가 방금 삭제한 "내용키"면 재삽입 차단 ──
        const contentKey = makeContentKey(newItem);
        if (pendingDeleteRef.current.has(contentKey)) {
          // 만약 보드가 진짜로 지워서 si 달고 왔다면 성공 처리하고 해제
          if (newItem.slotIndex != null) {
            pendingDeleteRef.current.delete(contentKey);
          }
          continue;
        }

        // ── ② 기존 무-si 항목과 매칭되면 si 업그레이드(병합) ──
        let merged = false;
        setScheduleLogs(prev => {
          const next = prev.map(old => {
            if (
              old.slotIndex == null &&
              makeContentKey(old) === contentKey &&
              newItem.slotIndex != null
            ) {
              merged = true;
              return { ...old, slotIndex: newItem.slotIndex };
            }
            return old;
          });
          return next;
        });
        if (merged) continue;

        // ── ③ 중복검사 후 추가 ──
        const key = makeKey(newItem);
        if (seenSetRef.current.has(key)) continue;
        seenSetRef.current.add(key);
        newItems.push(newItem);
      }

      if (newItems.length) {
        setScheduleLogs(prev => {
          const merged = [...prev, ...newItems];
          return merged.sort(
            (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
          );
        });
      }
    } catch (error) {
      console.error('Error parsing MQTT message:', error);
    }
  }, [mqttMessage, deviceMac, labelNo]);


  // MAC/핀 변경 시 초기화
  useEffect(() => {
    setScheduleLogs([]);
    seenSetRef.current.clear();
    isFirstListRef.current = true;
    if (email) handleView();
  }, [deviceMac, labelNo, email]);

  // 이메일 로드
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken: any = jwt_decode(token);
        setEmail(decodedToken.email);
      } catch (error) {
        console.error('토큰 디코딩 오류:', error);
      }
    } else {
      console.error('토큰이 없습니다.');
    }
  }, []);

  // 보기 요청
  const handleView = () => {
    isFirstListRef.current = true;
    publishSafe({
      command: 'schedule',
      operation: 'list',
      mac: deviceMac,
      pinIndex: labelNo,
    });
  };

  // 저장 요청
  const handleSave = () => {
    const [sH, sM] = startTime.split(':').map(Number);
    const [eH, eM] = endTime.split(':').map(Number);
    const start = sH * 60 + sM;
    const end = eH * 60 + eM;

    // ✅ 새 항목을 UI에 즉시 추가 (즉각 반영)
    const dayMap = ['일', '월', '화', '수', '목', '금', '토'];
    const z = (n: number) => String(n).padStart(2, '0');
    const normalizeDow = (v: any) => (Array.isArray(v) ? v : [v]);

    const startStr = `${z(sH)}:${z(sM)}`;
    const endStr = `${z(eH)}:${z(eM)}`;
    const repeatStr =
      repeatMode === 'weekly'
        ? normalizeDow(selectedDays)
            .map((d: number) => dayMap[d])
            .join(', ')
        : '매일';

    const newItem: ScheduleItem = {
      text: `${startStr} ~ ${endStr} ${repeatStr}`,
      slotIndex: null, // 실제 slotIndex는 보드 list 응답 후 업데이트됨
      startMinutes: start,
      endMinutes: end,
      repeatMode: repeatMode,
      dayOfWeek: repeatMode === 'weekly' ? selectedDays : 0,
    };

    // ✅ 중복 방지 로직에 따라 병합
    setScheduleLogs((prev) => {
      const merged = [...prev, newItem];
      return merged.sort(
        (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
      );
    });

    // ✅ 낙관 반영 항목도 중복키에 등록 (list 응답에서 중복 방지)
    seenSetRef.current.add(makeKey(newItem));

    // ✅ MQTT로 insert 메시지 발행
    const sendMessage: any = {
      command: 'schedule',
      operation: 'insert',
      mac: deviceMac,
      pinIndex: labelNo,
      startMinutes: start,
      endMinutes: end,
      repeatMode: repeatMode,
      dayOfWeek: repeatMode === 'weekly' ? selectedDays : 0,
    };
    publishSafe(sendMessage);

    // ✅ 서버의 실제 list 반영을 기다려 재동기화
    setTimeout(() => {
      handleView();
    }, 1200);
  };


  // 전체 삭제
  const handleDeleteAll = () => {
    publishSafe({
      command: 'schedule',
      mac: deviceMac,
      operation: 'deleteAll',
      pinIndex: labelNo,
    });
    setScheduleLogs([]);
    setTimeout(handleView, 500);
  };

  // 개별 삭제 (항목 객체 기반)
  const handleDelete = (item: ScheduleItem) => {
    // 1) 서버 삭제
    if (item.slotIndex == null) {
      // ⚠️ 보드가 si를 아직 안 준 경우(방금 추가된 항목 등) — 내용기반 삭제 시도
      publishSafe({
        command: 'schedule',
        mac: deviceMac,
        operation: 'delete',
        pinIndex: labelNo,
        // 서버가 내용기반 삭제를 지원하지 않으면, list 동기화만 유도
        // (가능하면 펌웨어가 slotIndex를 항상 내려주도록 맞추는 게 최선)
        startMinutes: item.startMinutes,
        endMinutes: item.endMinutes,
        repeatMode: item.repeatMode,
        dayOfWeek: item.dayOfWeek,
      });
      // 보드가 내용기반 삭제를 지원하지 않아도, 다음 list에서 재삽입을 차단
      pendingDeleteRef.current.add(makeContentKey(item));
    } else {
      // ✅ 권장: slotIndex로 정확 삭제
      publishSafe({
        command: 'schedule',
        mac: deviceMac,
        operation: 'delete',
        pinIndex: labelNo,
        slotIndex: item.slotIndex,
      });
    }

    // 2) UI 즉시 반영(고유키로 제거)
    const k = makeKey(item);
    setScheduleLogs(prev => prev.filter(p => makeKey(p) !== k));

    // 3) 서버 반영 확인 후 재동기화
    setTimeout(handleView, 600);
  };

  const toggleDaySelection = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  return (
    <div className="modal">
      <h3>스위치 {labelNo + 1}</h3>

      {/* 스케줄 목록 */}
      <div
        style={{
          maxHeight: 200,
          overflowY: 'auto',
          border: '1px solid #ccc',
          padding: 10,
          marginBottom: 10,
        }}
      >
        {scheduleLogs.map((item, index) => (
          <div
            key={
              item.slotIndex != null ? `si-${item.slotIndex}` : `idx-${index}`
            }
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <span style={{ minWidth: 60, fontWeight: 600 }}>
              {item.slotIndex != null ? `#${item.slotIndex}` : `[${index}]`}
            </span>
            <span style={{ flex: 1 }}>{item.text}</span>
            <button
              onClick={() => handleDelete(item)}
              title="삭제"
              style={{ cursor: 'pointer' }}
            >
              🗑️
            </button>
          </div>
        ))}
      </div>

      {/* 입력 필드 */}
      <div>
        <label>시작 시간:</label>
        <input
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
        />
      </div>

      <div>
        <label>종료 시간:</label>
        <input
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
        />
      </div>

      <div>
        <label>반복 설정:</label>
        <select
          value={repeatMode}
          onChange={(e) => setRepeatMode(e.target.value)}
        >
          <option value="daily">매일</option>
          <option value="weekly">매주</option>
        </select>
      </div>

      {repeatMode === 'weekly' && (
        <div>
          <label>요일 선택:</label>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              marginTop: '10px',
            }}
          >
            {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
              <button
                key={index}
                className={selectedDays.includes(index) ? 'selected-day' : ''}
                onClick={() => toggleDaySelection(index)}
                style={{
                  padding: '5px 10px',
                  border: '1px solid #ccc',
                  background: selectedDays.includes(index)
                    ? '#007bff'
                    : '#fff',
                  color: selectedDays.includes(index) ? '#fff' : '#000',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 버튼 */}
      <div style={{ marginTop: '20px' }}>
        <button onClick={handleView}>보기</button>
        <button onClick={handleSave}>저장</button>
        <button onClick={handleDeleteAll}>모두 지우기</button>
        <button onClick={onCancel}>취소</button>
      </div>
    </div>
  );
};

export default ModalSwitch;
